import * as THREE from 'three';
import { BonePose, type Bones, type Clip } from './clip.ts';

/**
 * The animation atlas: every clip of every rigged model, sampled at `ANIM_FPS`, in one float texture that all
 * skinned materials share. Each sample of each bone is two texels: its rotation (quaternion) and its
 * translation with a uniform scale in w, both relative to the rest pose in model space
 * (p' = q·(p·s) + t). The vertex shader picks the texels by the instance's clip and time (see skin.ts).
 */
export const ANIM_FPS = 60;
const W = 2048;

let rows = 64;
let data = new Float32Array(W * rows * 4);
let used = 0;

const makeTex = (): THREE.DataTexture => {
  const t = new THREE.DataTexture(data, W, rows, THREE.RGBAFormat, THREE.FloatType);
  t.minFilter = t.magFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.flipY = false;
  t.needsUpdate = true;
  return t;
};

/** Texels in use (dev stats). */
export const atlasUsage = (): { texels: number; bytes: number } => ({ texels: used, bytes: W * rows * 16 });

/** Shared by every skinned material: grows by swapping the texture. */
export const animTexture = { value: makeTex() };

const alloc = (texels: number): number => {
  const base = used;
  used += texels;
  if (used > W * rows) {
    while (used > W * rows) rows *= 2;
    const next = new Float32Array(W * rows * 4);
    next.set(data);
    data = next;
    const old = animTexture.value;
    animTexture.value = makeTex();
    old.dispose();
  }
  return base;
};

export interface ClipInfo {
  name: string;
  /** Spanish name for the animation sheet. */
  label?: string;
  /** First texel of the clip. */
  base: number;
  frames: number;
  dur: number;
  loop: boolean;
  bones: number;
  /** [sx, sy, sz] per frame when the clip squashes. */
  squash: Float32Array | null;
}

export interface AnimSet {
  key: string;
  bones: number;
  names: string[];
  clips: Map<string, ClipInfo>;
  /** The rest pose (one frame, identity): what an instance shows with no clip. */
  rest: ClipInfo;
}

const SETS = new Map<string, AnimSet>();

export const animSet = (key: string): AnimSet | undefined => SETS.get(key);

/**
 * Samples `clips` on a rig. `bones` are the pivots in index order (bone 0 is usually the model's inner group);
 * `names` name them for the clips. Cached by `key`: models that share a rig and clips share the samples.
 */
export const bakeAnim = (key: string, root: THREE.Object3D, bones: THREE.Object3D[], names: string[], clips: Clip[]): AnimSet => {
  const hit = SETS.get(key);
  if (hit) return hit;
  const n = bones.length;
  const rest = bones.map((b) => ({ p: b.position.clone(), q: b.quaternion.clone(), s: b.scale.clone() }));
  root.updateMatrixWorld(true);
  const rootInv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const restInv = bones.map((b) => new THREE.Matrix4().multiplyMatrices(rootInv, b.matrixWorld).invert());
  const poses: Record<string, BonePose> = {};
  for (const nm of names) poses[nm] = new BonePose();
  const scratch = new BonePose();
  const handle = new Proxy(poses, { get: (o, k: string) => o[k] ?? (scratch.reset(), scratch) }) as Bones;
  const e = new THREE.Euler();
  const dq = new THREE.Quaternion();
  const m = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scl = new THREE.Vector3();
  const prevQ = new Float32Array(n * 4);

  const sample = (c: Clip | null, t: number, out: number, first: boolean): void => {
    for (const p of Object.values(poses)) p.reset();
    if (c) c.pose(handle, t);
    bones.forEach((b, i) => {
      const r = rest[i] as (typeof rest)[number];
      const bp = poses[names[i] as string];
      if (!bp) {
        b.position.copy(r.p);
        b.quaternion.copy(r.q);
        b.scale.copy(r.s);
        return;
      }
      b.position.set(r.p.x + bp.px, r.p.y + bp.py, r.p.z + bp.pz);
      e.set(bp.rx, bp.ry, bp.rz, 'XYZ');
      b.quaternion.copy(r.q).multiply(dq.setFromEuler(e));
      b.scale.copy(r.s).multiplyScalar(bp.s);
    });
    root.updateMatrixWorld(true);
    for (let i = 0; i < n; i++) {
      m.multiplyMatrices(rootInv, (bones[i] as THREE.Object3D).matrixWorld).multiply(restInv[i] as THREE.Matrix4);
      m.decompose(pos, quat, scl);
      // Keep each bone's quaternion on the same hemisphere from frame to frame, so interpolation takes the short way.
      if (!first && quat.x * prevQ[i * 4]! + quat.y * prevQ[i * 4 + 1]! + quat.z * prevQ[i * 4 + 2]! + quat.w * prevQ[i * 4 + 3]! < 0) {
        quat.set(-quat.x, -quat.y, -quat.z, -quat.w);
      }
      prevQ.set([quat.x, quat.y, quat.z, quat.w], i * 4);
      const o = (out + i * 2) * 4;
      data[o] = quat.x;
      data[o + 1] = quat.y;
      data[o + 2] = quat.z;
      data[o + 3] = quat.w;
      data[o + 4] = pos.x;
      data[o + 5] = pos.y;
      data[o + 6] = pos.z;
      data[o + 7] = (scl.x + scl.y + scl.z) / 3;
    }
  };

  const record = (c: Clip | null, name: string, dur: number, loop: boolean): ClipInfo => {
    const frames = c ? Math.max(2, Math.round(dur * ANIM_FPS) + 1) : 1;
    const base = alloc(frames * n * 2);
    let squash: Float32Array | null = null;
    if (c?.squash) squash = new Float32Array(frames * 3);
    for (let f = 0; f < frames; f++) {
      const t = c ? Math.min(dur, (f / (frames - 1)) * dur) : 0;
      sample(c, t, base + f * n * 2, f === 0);
      if (squash && c?.squash) squash.set(c.squash(t), f * 3);
    }
    return { name, base, frames, dur, loop, bones: n, squash };
  };

  const set: AnimSet = { key, bones: n, names, clips: new Map(), rest: record(null, 'rest', 0, true) };
  for (const c of clips) {
    const info = record(c, c.name, c.dur, c.loop);
    if (c.label) info.label = c.label;
    set.clips.set(c.name, info);
  }
  // Leave the rig at rest for whoever bakes the geometry next.
  bones.forEach((b, i) => {
    const r = rest[i] as (typeof rest)[number];
    b.position.copy(r.p);
    b.quaternion.copy(r.q);
    b.scale.copy(r.s);
  });
  root.updateMatrixWorld(true);
  animTexture.value.needsUpdate = true;
  SETS.set(key, set);
  return set;
};

/** What an instance plays: clip `a` at `ta` seconds, cross-faded with `b` at `tb` by weight `w` (0: all `a`). */
export interface AnimParams {
  a: ClipInfo;
  ta: number;
  b: ClipInfo | null;
  tb: number;
  w: number;
}

/** Frame position of a clip at `t` seconds (wrapped for loops, held at the end otherwise). */
export const framePos = (c: ClipInfo, t: number): number => {
  if (c.frames < 2 || c.dur <= 0) return 0;
  let k = t / c.dur;
  if (c.loop) k -= Math.floor(k);
  else k = k < 0 ? 0 : k > 1 ? 1 : k;
  return k * (c.frames - 1);
};

/**
 * A bone's transform for an instance, as the shader computes it (normalized lerp between the two samples
 * around the frame, and between the two clips while they cross-fade): quaternion in q[0..3], translation in
 * q[4..6] and scale in q[7].
 */
export const poseBone = (p: AnimParams, bone: number, out: Float32Array): Float32Array => {
  sampleInto(p.a, framePos(p.a, p.ta), bone, out, 0);
  if (p.b && p.w > 0.001) {
    sampleInto(p.b, framePos(p.b, p.tb), bone, tmpB, 0);
    blendInto(out, tmpB, p.w);
  }
  return out;
};

const tmpB = new Float32Array(8);

const sampleInto = (c: ClipInfo, f: number, bone: number, out: Float32Array, o: number): void => {
  const f0 = Math.floor(f);
  const k = f - f0;
  const f1 = f0 + 1 < c.frames ? f0 + 1 : c.frames - 1;
  const i0 = (c.base + (f0 * c.bones + bone) * 2) * 4;
  const i1 = (c.base + (f1 * c.bones + bone) * 2) * 4;
  for (let j = 0; j < 8; j++) out[o + j] = data[i0 + j]! + (data[i1 + j]! - data[i0 + j]!) * k;
  const n = Math.hypot(out[o]!, out[o + 1]!, out[o + 2]!, out[o + 3]!) || 1;
  for (let j = 0; j < 4; j++) out[o + j]! /= n;
};

const blendInto = (a: Float32Array, b: Float32Array, w: number): void => {
  const s = a[0]! * b[0]! + a[1]! * b[1]! + a[2]! * b[2]! + a[3]! * b[3]! < 0 ? -1 : 1;
  for (let j = 0; j < 4; j++) a[j] = a[j]! + (b[j]! * s - a[j]!) * w;
  for (let j = 4; j < 8; j++) a[j] = a[j]! + (b[j]! - a[j]!) * w;
  const n = Math.hypot(a[0]!, a[1]!, a[2]!, a[3]!) || 1;
  for (let j = 0; j < 4; j++) a[j]! /= n;
};

/** Applies a bone transform from `poseBone` to a point (model space, rest pose). */
export const movePoint = (bt: Float32Array, v: THREE.Vector3): THREE.Vector3 => {
  const qx = bt[0]!, qy = bt[1]!, qz = bt[2]!, qw = bt[3]!, s = bt[7]!;
  const x = v.x * s, y = v.y * s, z = v.z * s;
  // v + 2·q×(q×v + w·v)
  const cx = qy * z - qz * y + qw * x, cy = qz * x - qx * z + qw * y, cz = qx * y - qy * x + qw * z;
  v.set(x + 2 * (qy * cz - qz * cy) + bt[4]!, y + 2 * (qz * cx - qx * cz) + bt[5]!, z + 2 * (qx * cy - qy * cx) + bt[6]!);
  return v;
};

/** Moves a point of bone `bone` (model space, rest pose) to where `p` puts it: what the shader does, for glows. */
export const posePoint = (p: AnimParams, bone: number, v: THREE.Vector3): THREE.Vector3 => movePoint(poseBone(p, bone, tmpP), v);
const tmpP = new Float32Array(8);

/** Whole-figure squash at the instance's time, written into `out` (1,1,1 when the clips don't squash). */
export const squashAt = (p: AnimParams, out: THREE.Vector3): THREE.Vector3 => {
  out.set(1, 1, 1);
  const one = (c: ClipInfo, t: number, w: number): void => {
    if (!c.squash) return;
    const f = framePos(c, t);
    const f0 = Math.floor(f);
    const k = f - f0;
    const f1 = Math.min(f0 + 1, c.frames - 1);
    for (let i = 0; i < 3; i++) {
      const v = c.squash[f0 * 3 + i]! + (c.squash[f1 * 3 + i]! - c.squash[f0 * 3 + i]!) * k;
      const cur = i === 0 ? out.x : i === 1 ? out.y : out.z;
      const next = cur + (v - 1) * w;
      if (i === 0) out.x = next;
      else if (i === 1) out.y = next;
      else out.z = next;
    }
  };
  one(p.a, p.ta, p.b ? 1 - p.w : 1);
  if (p.b) one(p.b, p.tb, p.w);
  return out;
};

/** An AnimSet as it crosses to another thread: its samples in one array, each clip's base counted from 0. */
export interface AnimWire {
  key: string;
  bones: number;
  names: string[];
  clips: ClipInfo[];
  samples: Float32Array;
}

/** Copies `s`'s samples out of the atlas (the bake worker sends them; adoptAnim takes them in). */
export const exportAnim = (s: AnimSet): AnimWire => {
  const infos = [s.rest, ...s.clips.values()];
  const samples = new Float32Array(infos.reduce((n, c) => n + c.frames * c.bones * 2 * 4, 0));
  let at = 0;
  const clips = infos.map((c) => {
    const len = c.frames * c.bones * 2;
    samples.set(data.subarray(c.base * 4, (c.base + len) * 4), at * 4);
    const out = { ...c, base: at };
    at += len;
    return out;
  });
  return { key: s.key, bones: s.bones, names: s.names, clips, samples };
};

/** Takes in a set sampled on another thread (the first one in wins, as with bakeAnim). */
export const adoptAnim = (w: AnimWire): AnimSet => {
  const hit = SETS.get(w.key);
  if (hit) return hit;
  const base = alloc(w.samples.length / 4);
  data.set(w.samples, base * 4);
  const [rest, ...clips] = w.clips.map((c) => ({ ...c, base: base + c.base }));
  const set: AnimSet = { key: w.key, bones: w.bones, names: w.names, clips: new Map(clips.map((c) => [c.name, c])), rest: rest as ClipInfo };
  animTexture.value.needsUpdate = true;
  SETS.set(w.key, set);
  return set;
};
